"use client";

import { useTranslation } from "react-i18next";
import { cn } from "@wondermaps/ui";
import type { HoursConfig, SeasonDates } from "@wondermaps/db";
import { computeOpenStatus } from "@wondermaps/maps";

type POIStatus = "normal" | "temp_closed" | "seasonal";

interface StatusBadgeProps {
  status: string;
  statusNote: string | null;
  hours?: HoursConfig | null;
  seasonDates?: SeasonDates | null;
  /** Hide badge when status is normal and hours is always_open (to avoid redundancy with HoursDisplay) */
  hideWhenAlwaysOpen?: boolean;
  /** Link to an external status-note element rendered outside the badge */
  "aria-describedby"?: string;
}

const STATUS_STYLES: Record<string, { bgColor: string; textColor: string; key: string }> = {
  open_24_7: { bgColor: "bg-green-100", textColor: "text-green-800", key: "status.open24_7" },
  open_now: { bgColor: "bg-green-100", textColor: "text-green-800", key: "status.openNow" },
  closed: { bgColor: "bg-red-100", textColor: "text-red-800", key: "status.closed" },
  hours_vary: { bgColor: "bg-gray-100", textColor: "text-gray-700", key: "status.hoursVary" },
  temp_closed: { bgColor: "bg-amber-100", textColor: "text-amber-800", key: "status.tempClosed" },
  closed_for_season: { bgColor: "bg-gray-100", textColor: "text-gray-600", key: "status.closedForSeason" },
  unknown: { bgColor: "bg-gray-100", textColor: "text-gray-600", key: "status.unknown" },
};

/**
 * Formats season dates for display
 * e.g., "05-01" to "10-31" -> "May 1 - Oct 31"
 */
function formatSeasonDates(seasonDates: SeasonDates, locale: string): string {
  const formatDate = (mmdd: string) => {
    const [month, day] = mmdd.split("-").map(Number);
    const date = new Date(2024, month - 1, day);
    return new Intl.DateTimeFormat(locale, { month: "short", day: "numeric" }).format(date);
  };
  return `${formatDate(seasonDates.start)} - ${formatDate(seasonDates.end)}`;
}

/**
 * Displays POI open/closed status as a colored badge.
 * Computes status from hours schedule and season dates.
 */
export function StatusBadge({
  status,
  statusNote,
  hours,
  seasonDates,
  hideWhenAlwaysOpen = false,
  "aria-describedby": ariaDescribedBy,
}: StatusBadgeProps) {
  const { t, i18n } = useTranslation("pois");
  // Compute the actual open status
  const openStatus = computeOpenStatus(
    status as POIStatus,
    hours,
    seasonDates
  );

  // Hide badge if always open and hideWhenAlwaysOpen is true
  if (hideWhenAlwaysOpen && openStatus.type === "open_24_7") {
    return null;
  }

  const style = STATUS_STYLES[openStatus.type] ?? STATUS_STYLES.unknown;
  const label = t(style.key);

  return (
    <div
      className="flex flex-col gap-1"
      aria-label={t("status.ariaStatus", { label: `${label}${statusNote ? `, ${statusNote}` : ""}` })}
      aria-describedby={ariaDescribedBy}
    >
      <span
        className={cn(
          "inline-flex items-center self-start rounded-full px-2.5 py-0.5 text-xs font-semibold",
          style.bgColor,
          style.textColor
        )}
      >
        {label}
      </span>
      {/* Show season dates for seasonal POIs */}
      {status === "seasonal" && seasonDates && (
        <span className="text-xs text-muted-foreground">
          {t("status.season", { dates: formatSeasonDates(seasonDates, i18n.language) })}
        </span>
      )}
      {statusNote && (
        <span className="text-xs text-muted-foreground">{statusNote}</span>
      )}
    </div>
  );
}
